import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import {
  MOTIVO_DESCARTE_LABELS,
  formatBRL,
  formatDate,
  type RadarOportunidade,
} from '@/pages/painel/lib/radarConstants';

export default function RadarArquivadas() {
  const { password } = useOutletContext<{ password: string }>();
  const [oportunidades, setOportunidades] = useState<RadarOportunidade[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('radar-admin', {
        body: { action: 'listar', password, filtros: { status: 'descartada' } },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { oportunidades?: RadarOportunidade[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setOportunidades(parsed?.oportunidades ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar arquivadas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  return (
    <div className="space-y-5">
      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

      {loading && oportunidades.length === 0 ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : oportunidades.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-2xl border border-gray-200">
          <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
            <i className="ri-archive-line text-xl text-gray-400"></i>
          </div>
          <p className="text-sm text-gray-500 mb-1">Nenhuma oportunidade descartada.</p>
          <p className="text-xs text-gray-400">
            Oportunidades marcadas como "Descartada" aparecem aqui, com o motivo do descarte.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50/80 text-gray-500 text-xs font-semibold uppercase tracking-wider">
                  <th className="px-4 py-3 text-left">Oportunidade</th>
                  <th className="px-4 py-3 text-left">Motivo do descarte</th>
                  <th className="px-4 py-3 text-left">Localização</th>
                  <th className="px-4 py-3 text-right">Valor</th>
                  <th className="px-4 py-3 text-left">Descoberto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {oportunidades.map((o) => (
                  <tr key={o.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <Link
                        to={`/painel/radar/oportunidades/${o.id}`}
                        className="font-semibold text-gray-900 hover:text-[#E8610A] transition-colors"
                      >
                        {o.titulo}
                      </Link>
                      {o.empresa && <div className="text-xs text-gray-500">{o.empresa}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold text-gray-600 bg-gray-100 rounded-full px-2.5 py-1 whitespace-nowrap">
                        {MOTIVO_DESCARTE_LABELS[o.motivo_descarte ?? ''] ?? o.motivo_descarte ?? 'Outro'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{[o.cidade, o.uf].filter(Boolean).join(' - ') || '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">
                      {formatBRL(o.valor_potencial_hop ?? o.valor_obra)}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{formatDate(o.data_descoberta ?? o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}